// Calendar-date arithmetic on `YYYY-MM-DD` strings (ENGINE_RULES §2). A calendar date is a day number, not an
// instant: every helper here works on whole days through `Date.UTC` and never consults a time zone, so DST
// days (23 or 25 hours) count as one day and `new Date('2026-03-08')` (the 7th in Chicago) never happens.
// The ONE place a time zone is consulted is `accountToday` in periods.ts.

import type { IsoDate } from '../types/primitives';

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const MS_PER_DAY = 86_400_000;

export interface CalendarDate {
  year: number;
  month: number; // 1–12
  day: number; // 1–31
}

/** Parse `YYYY-MM-DD`; throws on anything else, including an impossible day such as `2026-02-30`. */
export function parseIsoDate(iso: IsoDate): CalendarDate {
  const m = ISO_DATE.exec(iso);
  if (!m) throw new RangeError(`"${iso}" is not a YYYY-MM-DD calendar date`);
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month))
    throw new RangeError(`"${iso}" is not a real calendar date`);
  return { year, month, day };
}

const pad2 = (n: number): string => (n < 10 ? `0${n}` : String(n));

export function formatIsoDate(date: CalendarDate): IsoDate {
  return `${String(date.year).padStart(4, '0')}-${pad2(date.month)}-${pad2(date.day)}`;
}

/** Days since 1970-01-01 (proleptic Gregorian) — the integer that makes day arithmetic exact. */
export function toEpochDay(iso: IsoDate): number {
  const { year, month, day } = parseIsoDate(iso);
  return Date.UTC(year, month - 1, day) / MS_PER_DAY;
}

export function fromEpochDay(epochDay: number): IsoDate {
  const d = new Date(epochDay * MS_PER_DAY);
  return formatIsoDate({
    year: d.getUTCFullYear(),
    month: d.getUTCMonth() + 1,
    day: d.getUTCDate(),
  });
}

/** `addDays('2026-03-01', -1)` → `'2026-02-28'`. */
export function addDays(iso: IsoDate, days: number): IsoDate {
  return fromEpochDay(toEpochDay(iso) + days);
}

/** Inclusive day count of `[since, until]`: `daysInclusive('2026-09-15', '2026-09-21')` → 7. Negative when reversed. */
export function daysInclusive(since: IsoDate, until: IsoDate): number {
  return toEpochDay(until) - toEpochDay(since) + 1;
}

/** `compareIsoDates(a, b)` < 0 when `a` is earlier — plain string order is correct for `YYYY-MM-DD`. */
export function compareIsoDates(a: IsoDate, b: IsoDate): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate(); // day 0 of the next month = last day of this one
}

export function firstOfMonth(iso: IsoDate): IsoDate {
  const { year, month } = parseIsoDate(iso);
  return formatIsoDate({ year, month, day: 1 });
}

export function lastOfMonth(iso: IsoDate): IsoDate {
  const { year, month } = parseIsoDate(iso);
  return formatIsoDate({ year, month, day: daysInMonth(year, month) });
}

/**
 * Days left in `iso`'s calendar month, **today included** — 9 on Sep 22 of a 30-day month, 1 on the last day,
 * never 0 (REQ-076 AC4's "for the rest of the month").
 *
 * T-264 brought this here from `apps/web/src/routes/entity/entityModel.ts` and
 * `apps/mobile/src/screens/EntityScreen.model.ts`, where the same seven lines existed twice under the same
 * name. It is a CALENDAR rule, so it belongs beside the other three (`daysInMonth`, `firstOfMonth`,
 * `lastOfMonth`) and is decided in whole days through `Date.UTC`: a DST day is one day, and a month is as
 * long as the calendar says (February 2028 has 29).
 *
 * It answers a length, not a forecast. **Multiplying it by a budget is a forecast, and that belongs to the
 * server** (ARCH §2.3) — which is the other half of T-264: `BudgetInfo.restOfMonth` carries both the count
 * and the amount, so no app does the multiplication that made these two copies exist.
 */
export function daysLeftInMonth(iso: IsoDate): number {
  const { year, month, day } = parseIsoDate(iso);
  return daysInMonth(year, month) - day + 1;
}

/** The calendar month before the one `iso` is in, as `[first, last]`. */
export function previousMonth(iso: IsoDate): { since: IsoDate; until: IsoDate } {
  const lastOfPrevious = addDays(firstOfMonth(iso), -1);
  return { since: firstOfMonth(lastOfPrevious), until: lastOfPrevious };
}
